class WinnersAutoFill {
    static COMPANY_NAME = 'Roche';
    static COMPANY_NAME_NORMALIZED = WinnersAutoFill.COMPANY_NAME.toLowerCase();
    static DEFAULT_WINNERS_COUNT = 3;

    constructor({
        tenderData = window.tenderData,
        winnersCount = WinnersAutoFill.DEFAULT_WINNERS_COUNT
    } = {}) {
        this.tenderData = tenderData;
        this.winnersCount = winnersCount;
        this.bindEvents();
    }

    bindEvents() {
        // Listen for product changes to update autofill data
        const productSelect = document.querySelector('select[name="product_id"]');
        const priceInput = document.querySelector('input[name="offer_price_per_unit"]');
        const unitsInput = document.querySelector('input[name="number_of_units"]');

        const updateAutofills = () => {
            // Re-autofill all winner rows that have "Roche" filled in
            for (let i = 1; i <= this.winnersCount; i++) {
                const companyInput = document.getElementById(`winner-${i}-company`);
                if (!companyInput) continue;

                const value = this.normalize(companyInput.value);
                if (this.isTargetCompany(value)) {
                    this.autofill(i);
                }
            }
        };

        // Watch for changes in product selection and pricing
        if (productSelect) {
            productSelect.addEventListener('change', updateAutofills);
        }
        if (priceInput) {
            priceInput.addEventListener('input', updateAutofills);
            priceInput.addEventListener('change', updateAutofills);
        }
        if (unitsInput) {
            unitsInput.addEventListener('input', updateAutofills);
            unitsInput.addEventListener('change', updateAutofills);
        }

        // Bind company input events for each winner
        for (let i = 1; i <= this.winnersCount; i++) {
            const companyInput = document.getElementById(`winner-${i}-company`);
            if (!companyInput) continue;

            const check = (event) => {
                if (event && event.isTrusted === false) return;

                const value = this.normalize(companyInput.value);
                if (this.isTargetCompany(value)) {
                    this.autofill(i);
                }
            };

            companyInput.addEventListener('input', check);
            companyInput.addEventListener('blur', check);
        }
    }

    normalize(value) {
        return String(value ?? '').trim().toLowerCase();
    }

    isTargetCompany(normalized) {
        return normalized === WinnersAutoFill.COMPANY_NAME_NORMALIZED;
    }

    async autofill(index) {
        const productId = this.getProductId();
        const offerPricePerUnit = this.getOfferPricePerUnit();

        this.updateField(`winner-${index}-company`, WinnersAutoFill.COMPANY_NAME);

        // If we have a product selected, fetch fresh data from API
        if (productId) {
            try {
                const productData = await this.fetchProductData(productId);
                this.updateField(
                    `winner-${index}-product`,
                    productData.product_name_supplement?.trim() || productData.name?.trim() || ''
                );
                // Use API price if offerPricePerUnit is not set
                const price = offerPricePerUnit || productData.price || 0;
                this.updateField(
                    `winner-${index}-price`,
                    this.formatPrice(price)
                );
            } catch (error) {
                console.error('Failed to fetch product data:', error);
                // Fallback to manual input values
                this.updateField(
                    `winner-${index}-product`,
                    this.getProductName()?.trim() || ''
                );
                this.updateField(
                    `winner-${index}-price`,
                    this.formatPrice(offerPricePerUnit)
                );
            }
        } else {
            // No product selected, use manual values
            this.updateField(
                `winner-${index}-product`,
                this.getProductName()?.trim() || ''
            );
            this.updateField(
                `winner-${index}-price`,
                this.formatPrice(offerPricePerUnit)
            );
        }
    }

    async fetchProductData(productId) {
        const response = await fetch(`/products/${productId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result.data;
    }

    getProductId() {
        const productSelect = document.querySelector('select[name="product_id"]');
        if (productSelect && productSelect.value) {
            return productSelect.value;
        }

        // Fallback to tenderData if available
        if (this.tenderData?.productId) {
            return this.tenderData.productId;
        }

        return null;
    }

    getProductName() {
        const productSelect = document.querySelector('select[name="product_id"]');
        if (productSelect && productSelect.selectedIndex > 0) {
            const selectedOption = productSelect.options[productSelect.selectedIndex];
            return selectedOption.text;
        }

        if (this.tenderData?.productName) {
            return this.tenderData.productName;
        }

        return '';
    }

    getOfferPricePerUnit() {
        const priceInput = document.querySelector('input[name="offer_price_per_unit"]');
        if (priceInput) {
            const hiddenInput = priceInput.id ? document.getElementById(priceInput.id + '-hidden') : null;
            if (hiddenInput && hiddenInput.value) {
                return this.parseNumber(hiddenInput.value);
            }
            return this.parseNumber(priceInput.value);
        }

        if (this.tenderData?.offerPricePerUnit) {
            return this.tenderData.offerPricePerUnit;
        }

        return 0;
    }

    getNumberOfUnits() {
        if (this.tenderData?.numberOfUnits) {
            return this.tenderData.numberOfUnits;
        }

        const unitsInput = document.querySelector('input[name="number_of_units"]');
        if (unitsInput) {
            const hiddenInput = unitsInput.id ? document.getElementById(unitsInput.id + '-hidden') : null;
            if (hiddenInput && hiddenInput.value) {
                return this.parseNumber(hiddenInput.value);
            }
            return this.parseNumber(unitsInput.value);
        }

        return 1;
    }

    parseNumber(value) {
        if (!value || typeof value !== 'string') {
            return Number(value) || 0;
        }

        value = value.replace(/\s/g, '')
            .replace(/[^0-9,.\-]/g, '')
            .replace(',', '.');

        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    }

    updateField(id, value) {
        const el = document.getElementById(id);
        if (!el) return;

        el.value = value;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
    }

    formatPrice(price) {
        return new Intl.NumberFormat('cs-CZ', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(price);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new WinnersAutoFill();
});
